import {
  DebtSaleAccountDetailsError,
  DebtSaleAccountDetailsResult,
  DebtSaleAccountDetailsResultTypenames,
  DebtSaleAccountDetailsSuccess,
} from '@varo/graphql-schema-types';
import FetchDebtSaleAccountDetails from 'services/graphql/queries/FetchDebtSaleAccountDetails';
import { CombinedError, useQuery } from 'urql';

export enum DebtSaleAccountDetailsStatus {
  Success = 'Success',
  Error = 'Error',
}

export type DebtSaleAccountDetailsSuccessSelection = Omit<
  DebtSaleAccountDetailsSuccess,
  'title'
> & {
  pageTitle: DebtSaleAccountDetailsSuccess['title'];
};

export type DebtSaleAccountDetailsState =
  | {
      status: DebtSaleAccountDetailsStatus.Success;
      success: DebtSaleAccountDetailsSuccessSelection;
    }
  | {
      status: DebtSaleAccountDetailsStatus.Error;
      typedError: DebtSaleAccountDetailsError;
    };

export interface UseDebtSaleAccountDetailsResult {
  result: DebtSaleAccountDetailsState | null;
  fetching: boolean;
  error: CombinedError | DebtSaleAccountDetailsError | null;
  refetch: () => void;
}

type DebtSaleAccountDetailsQueryResult =
  | Exclude<DebtSaleAccountDetailsResult, DebtSaleAccountDetailsSuccess>
  | DebtSaleAccountDetailsSuccessSelection;

const toDebtSaleAccountDetailsState = (
  debtSaleAccountDetails: DebtSaleAccountDetailsQueryResult | undefined,
): DebtSaleAccountDetailsState | null => {
  if (
    debtSaleAccountDetails?.__typename ===
    DebtSaleAccountDetailsResultTypenames.DebtSaleAccountDetailsSuccess
  ) {
    return {
      status: DebtSaleAccountDetailsStatus.Success,
      success: debtSaleAccountDetails,
    };
  }

  if (
    debtSaleAccountDetails?.__typename ===
    DebtSaleAccountDetailsResultTypenames.DebtSaleAccountDetailsError
  ) {
    return {
      status: DebtSaleAccountDetailsStatus.Error,
      typedError: debtSaleAccountDetails,
    };
  }

  return null;
};

const useDebtSaleAccountDetails = (
  accountId: string,
): UseDebtSaleAccountDetailsResult => {
  const trimmedAccountId = accountId.trim();
  const hasAccountId = trimmedAccountId.length > 0;

  const [{ data, fetching, error }, refetch] = useQuery<{
    debtSaleAccountDetails: DebtSaleAccountDetailsQueryResult;
  }>({
    query: FetchDebtSaleAccountDetails,
    variables: { accountId: trimmedAccountId },
    requestPolicy: 'cache-and-network',
    pause: !hasAccountId,
  });

  const result = toDebtSaleAccountDetailsState(data?.debtSaleAccountDetails);

  const typedError =
    result?.status === DebtSaleAccountDetailsStatus.Error
      ? result.typedError
      : null;

  return {
    result,
    fetching: fetching && !data,
    error: error ?? typedError,
    refetch,
  };
};

export default useDebtSaleAccountDetails;
